import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Divider,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Science,
  LocalHospital,
  Medication,
  Vaccines,
  Assignment,
  Description,
  Image,
  Timeline,
  Download,
  Visibility,
  MoreVert,
  FolderOpen,
  Search,
  CalendarToday,
} from "@mui/icons-material";
import { themeV2 } from "../../../themeV2";

// Date filter options
const dateFilterOptions = [
  { value: "all-time", label: "All Time" },
  { value: "last-6-months", label: "Last 6 Months" },
  { value: "last-month", label: "Last Month" },
  { value: "last-year", label: "Last Year" },
];

// Health record types with icons
const healthRecordTypes = [
  {
    id: "all-records",
    label: "All Records",
    icon: FolderOpen,
    count: 56,
    isAllRecords: true,
  },
  { id: "lab-results", label: "Lab Results", icon: Science, count: 12 },
  { id: "diagnoses", label: "Diagnoses", icon: Assignment, count: 3 },
  { id: "medications", label: "Medications", icon: Medication, count: 8 },
  { id: "immunizations", label: "Immunizations", icon: Vaccines, count: 5 },
  { id: "procedures", label: "Procedures", icon: LocalHospital, count: 2 },
  { id: "documents", label: "Documents", icon: Description, count: 7 },
  { id: "images", label: "Images", icon: Image, count: 4 },
  { id: "timeline", label: "Timeline", icon: Timeline, count: 15 },
];

// Sample lab results data
const labResultsData = [
  {
    id: 1,
    testName: "Complete Blood Count (CBC)",
    date: "2024-01-15",
    status: "Normal",
    statusColor: "success" as const,
    results: [
      {
        name: "White Blood Cells",
        value: "7.2",
        unit: "K/μL",
        range: "4.5-11.0",
        status: "normal",
      },
      {
        name: "Red Blood Cells",
        value: "4.8",
        unit: "M/μL",
        range: "4.0-5.2",
        status: "normal",
      },
      {
        name: "Hemoglobin",
        value: "14.2",
        unit: "g/dL",
        range: "12.0-16.0",
        status: "normal",
      },
      {
        name: "Hematocrit",
        value: "42.1",
        unit: "%",
        range: "36-46",
        status: "normal",
      },
      {
        name: "Platelets",
        value: "285",
        unit: "K/μL",
        range: "150-450",
        status: "normal",
      },
    ],
    provider: "Dr. Sarah Johnson",
    facility: "City Medical Center",
  },
  {
    id: 2,
    testName: "Comprehensive Metabolic Panel",
    date: "2024-01-15",
    status: "Abnormal",
    statusColor: "warning" as const,
    results: [
      {
        name: "Glucose",
        value: "95",
        unit: "mg/dL",
        range: "70-100",
        status: "normal",
      },
      {
        name: "Creatinine",
        value: "1.1",
        unit: "mg/dL",
        range: "0.6-1.2",
        status: "normal",
      },
      {
        name: "BUN",
        value: "18",
        unit: "mg/dL",
        range: "7-20",
        status: "normal",
      },
      {
        name: "Sodium",
        value: "142",
        unit: "mEq/L",
        range: "136-145",
        status: "normal",
      },
      {
        name: "Potassium",
        value: "3.8",
        unit: "mEq/L",
        range: "3.5-5.0",
        status: "normal",
      },
      {
        name: "ALT",
        value: "45",
        unit: "U/L",
        range: "7-56",
        status: "normal",
      },
      {
        name: "AST",
        value: "38",
        unit: "U/L",
        range: "10-40",
        status: "normal",
      },
    ],
    provider: "Dr. Sarah Johnson",
    facility: "City Medical Center",
  },
  {
    id: 3,
    testName: "Lipid Panel",
    date: "2024-01-10",
    status: "Normal",
    statusColor: "success" as const,
    results: [
      {
        name: "Total Cholesterol",
        value: "185",
        unit: "mg/dL",
        range: "<200",
        status: "normal",
      },
      {
        name: "HDL Cholesterol",
        value: "55",
        unit: "mg/dL",
        range: ">40",
        status: "normal",
      },
      {
        name: "LDL Cholesterol",
        value: "110",
        unit: "mg/dL",
        range: "<100",
        status: "high",
      },
      {
        name: "Triglycerides",
        value: "95",
        unit: "mg/dL",
        range: "<150",
        status: "normal",
      },
    ],
    provider: "Dr. Michael Chen",
    facility: "Wellness Clinic",
  },
  {
    id: 4,
    testName: "Thyroid Function Test",
    date: "2024-01-08",
    status: "Normal",
    statusColor: "success" as const,
    results: [
      {
        name: "TSH",
        value: "2.1",
        unit: "mIU/L",
        range: "0.4-4.0",
        status: "normal",
      },
      {
        name: "Free T4",
        value: "1.2",
        unit: "ng/dL",
        range: "0.8-1.8",
        status: "normal",
      },
      {
        name: "Free T3",
        value: "3.1",
        unit: "pg/mL",
        range: "2.3-4.2",
        status: "normal",
      },
    ],
    provider: "Dr. Emily Rodriguez",
    facility: "Endocrine Associates",
  },
];

const MyHealthRecords = () => {
  const [selectedType, setSelectedType] = useState("all-records");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all-time");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "normal":
        return themeV2.palette.success.main;
      case "high":
        return themeV2.palette.warning.main;
      case "low":
        return themeV2.palette.info.main;
      default:
        return themeV2.palette.text.secondary;
    }
  };

  const renderSearchAndFilterBar = () => (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            placeholder="Search records..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ color: "#64748b", mr: 1, fontSize: 20 }} />
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f8fafc",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel
              sx={{
                color: "#64748b",
                "&.Mui-focused": {
                  color: "#1d4ed8",
                },
              }}
            >
              Time Period
            </InputLabel>
            <Select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              label="Time Period"
              startAdornment={
                <CalendarToday sx={{ color: "#64748b", mr: 1, fontSize: 20 }} />
              }
              sx={{
                borderRadius: "12px",
                backgroundColor: "#f8fafc",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {dateFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderLabResults = () => {
    // Filter lab results based on search query
    const filteredResults = labResultsData.filter(
      (result) =>
        result.testName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        result.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
        result.facility.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <Box>
        {renderSearchAndFilterBar()}

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredResults.length} lab result
            {filteredResults.length !== 1 ? "s" : ""}
            {searchQuery && ` matching "${searchQuery}"`}
            {dateFilter !== "all-time" &&
              ` from ${dateFilterOptions
                .find((opt) => opt.value === dateFilter)
                ?.label.toLowerCase()}`}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {filteredResults.length > 0 ? (
            filteredResults.map((result) => (
              <Grid item xs={12} key={result.id}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 1, color: "#0f172a" }}
                        >
                          {result.testName}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {new Date(result.date).toLocaleDateString()}
                          </Typography>
                          <Chip
                            label={result.status}
                            size="small"
                            sx={{
                              backgroundColor:
                                result.statusColor === "success"
                                  ? "#f0fdf4"
                                  : "#fef3c7",
                              color:
                                result.statusColor === "success"
                                  ? "#15803d"
                                  : "#d97706",
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                          />
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" sx={{ color: "#64748b" }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" sx={{ color: "#64748b" }}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Options">
                          <IconButton size="small" sx={{ color: "#64748b" }}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      {result.results.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper
                            sx={{
                              p: 2,
                              backgroundColor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              borderRadius: "12px",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {item.name}
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: getStatusColor(item.status),
                                }}
                              >
                                {item.value}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.unit}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Range: {item.range}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Provider: {result.provider}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Facility: {result.facility}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "300px",
                  textAlign: "center",
                  p: 4,
                }}
              >
                <Search sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No lab results found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search terms or filters
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderAllRecords = () => {
    // Filter record types based on search query
    const filteredTypes = healthRecordTypes
      .filter((type) => !type.isAllRecords)
      .filter((type) =>
        type.label.toLowerCase().includes(searchQuery.toLowerCase())
      );

    return (
      <Box>
        {/* Search Bar for All Records */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search record types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ color: "#64748b", mr: 1, fontSize: 20 }} />
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "#f8fafc",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1d4ed8",
                },
              },
            }}
          />
        </Paper>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTypes.length} record type
            {filteredTypes.length !== 1 ? "s" : ""}
            {searchQuery && ` matching "${searchQuery}"`}
          </Typography>
        </Box>

        {/* Record Type Cards */}
        <Grid container spacing={3}>
          {filteredTypes.length > 0 ? (
            filteredTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={type.id}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                      transition: "all 0.2s ease-in-out",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={() => setSelectedType(type.id)}
                  >
                    <CardContent sx={{ p: 3, textAlign: "center" }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 1, color: "#0f172a" }}
                      >
                        {type.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {type.count} records available
                      </Typography>
                      <Chip
                        label="View Records"
                        size="small"
                        sx={{
                          backgroundColor: "#1d4ed8",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "300px",
                  textAlign: "center",
                  p: 4,
                }}
              >
                <Search sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No record types found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search terms
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderContent = () => {
    switch (selectedType) {
      case "all-records":
        return renderAllRecords();
      case "lab-results":
        return renderLabResults();
      default:
        return (
          <Box>
            {renderSearchAndFilterBar()}

            {/* Results Count */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Showing 0 records
                {searchQuery && ` matching "${searchQuery}"`}
                {dateFilter !== "all-time" &&
                  ` from ${dateFilterOptions
                    .find((opt) => opt.value === dateFilter)
                    ?.label.toLowerCase()}`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                textAlign: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: "#f1f5f9",
                  color: "#64748b",
                  mb: 2,
                }}
              >
                {React.createElement(
                  healthRecordTypes.find((t) => t.id === selectedType)?.icon ||
                    Science,
                  { fontSize: "large" }
                )}
              </Avatar>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {healthRecordTypes.find((t) => t.id === selectedType)?.label}{" "}
                Coming Soon
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This section is under development and will be available soon.
              </Typography>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#1e3a8a",
            mb: 1,
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
          }}
        >
          My Health Records
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#475569",
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          View and manage your comprehensive health records and medical data
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Side Panel */}
        <Grid item xs={12} md={3}>
          <Paper
            sx={{
              p: 2,
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              position: "sticky",
              top: 24,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "#0f172a" }}
            >
              Record Types
            </Typography>
            <List sx={{ p: 0 }}>
              {healthRecordTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <ListItem key={type.id} sx={{ p: 0, mb: 1 }}>
                    <ListItemButton
                      onClick={() => setSelectedType(type.id)}
                      selected={selectedType === type.id}
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        px: 2,
                        "&.Mui-selected": {
                          backgroundColor: "#eff6ff",
                          "&:hover": {
                            backgroundColor: "#dbeafe",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <IconComponent
                          sx={{
                            color:
                              selectedType === type.id ? "#1d4ed8" : "#64748b",
                            fontSize: 20,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={type.label}
                        secondary={`${type.count} records`}
                        primaryTypographyProps={{
                          fontSize: "14px",
                          fontWeight: selectedType === type.id ? 600 : 500,
                          color:
                            selectedType === type.id ? "#1d4ed8" : "#0f172a",
                        }}
                        secondaryTypographyProps={{
                          fontSize: "12px",
                          color: "#64748b",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#0f172a", mb: 1 }}
            >
              {healthRecordTypes.find((t) => t.id === selectedType)?.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedType === "all-records"
                ? "Browse all your health record categories and access specific record types"
                : selectedType === "lab-results"
                ? "View and manage your laboratory test results"
                : "View and manage your health records"}
            </Typography>
          </Box>
          {renderContent()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyHealthRecords;
